import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", icon: "package", features: [], sort_order: 0, active: true };

const ICONS = ["globe", "shopping-bag", "workflow", "zap", "trending-up", "wrench", "server", "plug", "package"];


export default function AdminServices() {
    const [list, setList] = useState([]);
    const [edit, setEdit] = useState(null);

    const load = () => api.get("/admin/services").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            const payload = {
                ...data,
                sort_order: Number(data.sort_order || 0),
                features: typeof data.features === "string"
                    ? data.features.split("\n").map((s) => s.trim()).filter(Boolean)
                    : (data.features || []),
            };
            if (data.id) await api.put(`/admin/services/${data.id}`, payload);
            else await api.post("/admin/services", payload);
            toast.success("Serviço guardado");
            setEdit(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar serviço?")) return;
        await api.delete(`/admin/services/${id}`);
        load();
    };

    return (
        <div data-testid="admin-services-page">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Serviços</h1>
                    <p className="text-sm text-ink/60 mt-1">{list.length} serviços exibidos em /servicos</p>
                </div>
                <button
                    onClick={() => setEdit({ ...EMPTY })}
                    className="btn-primary"
                    data-testid="new-service-btn"
                >
                    <Plus size={16} /> Novo serviço
                </button>
            </div>

            <div className="border-2 border-ink bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-paper border-b-2 border-ink">
                        <tr className="text-left">
                            <th className="p-3 font-mono uppercase text-xs">Ordem</th>
                            <th className="p-3 font-mono uppercase text-xs">Título</th>
                            <th className="p-3 font-mono uppercase text-xs">Ícone</th>
                            <th className="p-3 font-mono uppercase text-xs">Features</th>
                            <th className="p-3 font-mono uppercase text-xs">Activo</th>
                            <th className="p-3 font-mono uppercase text-xs text-right">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((s) => (
                            <tr key={s.id} className="border-b border-ink/10 hover:bg-paper" data-testid={`service-row-${s.id}`}>
                                <td className="p-3 font-mono">{s.sort_order}</td>
                                <td className="p-3 font-bold">{s.title}</td>
                                <td className="p-3 font-mono text-xs">{s.icon}</td>
                                <td className="p-3 text-xs">{s.features?.length || 0}</td>
                                <td className="p-3">
                                    <span className={`text-[10px] font-mono uppercase px-2 py-1 border-2 ${s.active ? "border-emerald-500 text-emerald-700" : "border-ink/30 text-ink/50"}`}>
                                        {s.active ? "Activo" : "Oculto"}
                                    </span>
                                </td>
                                <td className="p-3 text-right">
                                    <button onClick={() => setEdit(s)} className="border-2 border-ink p-2 hover:bg-ink hover:text-white mr-1" data-testid={`edit-${s.id}`}>
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(s.id)} className="border-2 border-rose-500 text-rose-500 p-2 hover:bg-rose-500 hover:text-white" data-testid={`delete-${s.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {edit && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
                    <div className="bg-white border-2 border-ink max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b-2 border-ink p-4 flex items-center justify-between">
                            <h2 className="font-display font-black text-xl tracking-tighter">{edit.id ? "Editar" : "Novo"} serviço</h2>
                            <button onClick={() => setEdit(null)}><X size={18} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <label className="block">
                                <div className="label-mono mb-2">Título *</div>
                                <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="input-edge" data-testid="srv-title" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Descrição *</div>
                                <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="input-edge min-h-[100px]" data-testid="srv-desc" />
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <label className="block">
                                    <div className="label-mono mb-2">Ícone</div>
                                    <select value={edit.icon} onChange={(e) => setEdit({ ...edit, icon: e.target.value })} className="input-edge">
                                        {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <div className="label-mono mb-2">Ordem</div>
                                    <input type="number" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} className="input-edge" />
                                </label>
                            </div>
                            <label className="block">
                                <div className="label-mono mb-2">Features (uma por linha)</div>
                                <textarea
                                    value={Array.isArray(edit.features) ? edit.features.join("\n") : edit.features}
                                    onChange={(e) => setEdit({ ...edit, features: e.target.value })}
                                    className="input-edge min-h-[120px]"
                                    placeholder="Design responsivo&#10;Painel CMS&#10;SEO técnico"
                                />
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
                                <span className="text-sm font-bold uppercase tracking-wider">Activo</span>
                            </label>
                        </div>
                        <div className="border-t-2 border-ink p-4 flex justify-end gap-2">
                            <button onClick={() => setEdit(null)} className="btn-secondary">Cancelar</button>
                            <button onClick={() => save(edit)} className="btn-primary" data-testid="srv-save"><Save size={16} /> Guardar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
